import { useState, useEffect } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { actions } from 'store/campaign';
import { produce } from 'immer';
import FormController from 'common/formController';
import campaignValidator from './validator';
import sessionHelper from 'helpers/sessionHelper';
import { constants } from 'views/Campaign';
import { setCurrentAttachmentTemplate } from 'store/attachmentTemplate/actions';
import {
  setCurrentTemplate,
  unsetCurrentTemplate
} from 'store/template/actions';
import { setCurrentQueryTemplate } from 'store/recipientQueryTemplate/actions';
import { ProductType } from 'constants.js';
import { getSchemas } from 'store/schema/actions';
import { convertToTimeOnlyString } from 'helpers/timeUtils';
const {
  campaignOccurrenceTypes: {
    ON_DEMAND,
    ON_EVENT,
    SCHEDULED,
    INTERNAL_SURVEY_RESPONSE,
    EXTERNAL_SURVEY_RESPONSE
  }
} = constants;

const {
  getCampaign,
  setCurrentCampaign,
  updateCampaign,
  createCampaign,
  resetScheduleNow
} = actions;

let formController = new FormController();

export default function useCampaign(nextPath, backPath) {
  const dispatch = useDispatch();
  const history = useHistory();
  const store = useStore();

  const { campaignUuid } = useParams();
  const { refreshSessionIfNeeded } = sessionHelper(store);
  const campaign = useSelector(store => store.campaign.current);

  const isCampaignGetting = useSelector(store => store.campaign.getting);
  const isCampaignUpdating = useSelector(store => store.campaign.updating);
  const isCampaignSaved = useSelector(store => store.campaign.isCampaignSaved);
  const isCampaignSaving = useSelector(
    store => store.campaign.isCampaignSaving
  );

  const supportedEventTypeOptions = useSelector(
    store => store.events.supportedEventTypes.options
  );

  const productIntegrationOptions = useSelector(
    store => store.events.supportedEventTypes.productIntegrationOptions
  );

  const supportedScheduleTypeOptions = useSelector(
    store => store.events.supportedScheduledTypes
  );

  const supportedOnDemandTypeOptions = useSelector(
    store => store.events.supportedOnDemandTypes
  );

  const supportedInternalSurveyResponseTypeOptions = useSelector(
    store => store.events.supportedInternalSurveyResponseTypes
  );

  const supportedExternalSurveyResponseTypeOptions = useSelector(
    store => store.events.supportedExternalSurveyResponseTypes
  );

  const gettingAttributesSelector = useSelector(
    store => store.campaign.gettingAttributes
  );
  const attachmentsSelector = useSelector(
    store => store.attachmentTemplate.list
  );

  const isScheduleNowProcessing = useSelector(
    store => store.campaign.isScheduleNowProcessing
  );

  const currentBusinessContextId = useSelector(
    store => store.user.selectedBusinessContext.businessContextId
  );

  const listManagementSchema = useSelector(
    store => store.schema.listManagementSchema
  );

  const guestInsightSchema = useSelector(
    store => store.schema.guestInsightSchema
  );

  const error = useSelector(store => store.campaign.error);
  const template = useSelector(store => store.template.current);
  const attachmentTemplate = useSelector(
    store => store.attachmentTemplate.current
  );

  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const [thisCampaign, setThisCampaign] = useState({
    name: '',
    type: '',
    emailCampaignData: {
      emailSubject: '',
      fromAddress: '',
      fromName: '',
      replyAddress: ''
    },
    backport: false,
    promotional: false,
    attachmentTemplateUuids: [],
    campaignOccurrenceType: '',
    startDate: null,
    endDate: null
  });

  const isCampaignReadOnly = () => {
    return (
      !isLoading &&
      campaign.uuid &&
      currentBusinessContextId !== campaign.businessContextId
    );
  };

  const validate = (saveAttempted = false, campaignUpdate) => {
    const validation = campaignValidator.validate(
      campaignUpdate || thisCampaign,
      {
        abortEarly: false,
        allowUnknown: true // allow unknown keys that will be ignored
      }
    );
    let errors = formController.getFormErrors(validation, { saveAttempted });
    setValidationErrors(errors);
    return errors;
  };

  const isItineraryOnly = () =>
    productIntegrationOptions &&
    productIntegrationOptions.length === 1 &&
    productIntegrationOptions[0] === 'itinerary';

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    dispatch(getCampaign({ campaignUuid }));
  }, []);

  useEffect(() => {
    if (attachmentsSelector.length > 0) {
      setAttachments(attachmentsSelector);
    }
  }, [attachmentsSelector]);

  useEffect(() => {
    if (Object.keys(campaign).length > 0) {
      formController = new FormController();
      validate();
      setThisCampaign({
        ...campaign,
        promotional:
          typeof campaign?.promotional === 'boolean'
            ? campaign.promotional
            : false,
        recurrence: campaign?.scheduleRule?.hasOwnProperty(
          'recurrenceExpression'
        )
      });
    }
    if (!campaignUuid) {
      setThisCampaign({
        ...campaign,
        promotional:
          typeof campaign?.promotional === 'boolean'
            ? campaign.promotional
            : false,
        hasMovedToNextStep: campaign.hasMovedToNextStep || false
      });
    }
    if (
      !template.schemaId ||
      template.campaignOccurrenceType !== campaign.campaignOccurrenceType
    ) {
      setCurrentTemplateSchemaId();
    }
  }, [campaign]);

  useEffect(() => {
    if (isItineraryOnly() && !thisCampaign.campaignOccurrenceType) {
      setThisCampaign({ ...thisCampaign, campaignOccurrenceType: ON_EVENT });
    }
  }, [thisCampaign]);

  useEffect(() => {
    if (
      productIntegrationOptions?.filter(option => option != 'itinerary')
        .length === 1 &&
      (thisCampaign.campaignOccurrenceType === ON_DEMAND ||
        thisCampaign.campaignOccurrenceType === SCHEDULED) &&
      thisCampaign.productType !== ProductType.List
    ) {
      handleProductTypeChange({
        target: { name: 'productName', value: productIntegrationOptions[0] }
      });
    }
  }, [thisCampaign.campaignOccurrenceType]);

  useEffect(() => {
    setIsLoading(
      isCampaignGetting ||
      isCampaignUpdating ||
      gettingAttributesSelector ||
      !!error.messages
    );
  }, [isCampaignGetting, isCampaignUpdating, gettingAttributesSelector, error]);

  useEffect(() => {
    if (isScheduleNowProcessing) {
      handleScheduleRuleChange({
        scheduleNow: true
      });
    }
  }, [isScheduleNowProcessing]);

  useEffect(() => {
    if (thisCampaign.scheduleRule?.scheduleNow && isScheduleNowProcessing) {
      saveCampaign();
    }
  }, [thisCampaign]);

  useEffect(() => {
    if (
      thisCampaign.productType &&
      thisCampaign.productType === ProductType.List
    ) {
      setThisCampaign({
        ...thisCampaign,
        productCategory: listManagementSchema?.eventProductCategory || '',
        productName: listManagementSchema?.eventProductName || '',
        eventTypeCategory: listManagementSchema?.eventTypeCategory || '',
        schemaIdentifier:
          listManagementSchema?.eventProductCategory +
          '.' +
          listManagementSchema?.eventProductName +
          '.' +
          listManagementSchema?.eventTypeCategory || ''
      });
    }
  }, [listManagementSchema]);

  useEffect(() => {
    if (
      thisCampaign.productType &&
      thisCampaign.productType === ProductType.GUEST_INSIGHTS
    ) {
      setThisCampaign({
        ...thisCampaign,
        productCategory: guestInsightSchema?.eventProductCategory || '',
        productName: guestInsightSchema?.eventProductName || '',
        eventTypeCategory: guestInsightSchema?.eventTypeCategory || '',
        schemaIdentifier:
        guestInsightSchema?.eventProductCategory +
          '.' +
          guestInsightSchema?.eventProductName +
          '.' +
          guestInsightSchema?.eventTypeCategory || ''
      });
    }
  }, [guestInsightSchema]);

  const setCurrentTemplateSchemaId = () => {
    let selectedOption;
    if (campaign.campaignOccurrenceType === SCHEDULED) {
      selectedOption = supportedScheduleTypeOptions.find(
        option => option.id === campaign.schemaIdentifier
      );
    } else if (campaign.campaignOccurrenceType === ON_DEMAND) {
      selectedOption = supportedOnDemandTypeOptions.find(
        option => option.id === campaign.schemaIdentifier
      );
    } else if (campaign.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE) {
      selectedOption = supportedInternalSurveyResponseTypeOptions.find(
        option => option.id === campaign.schemaIdentifier
      );
    } else if (campaign.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE) {
      selectedOption = supportedExternalSurveyResponseTypeOptions.find(
        option => option.id === campaign.schemaIdentifier
      );
    } else if (campaign.campaignOccurrenceType === ON_EVENT) {
      selectedOption = supportedEventTypeOptions.find(
        option => option.id === campaign.schemaIdentifier
      );
    }
    if (selectedOption) {
      dispatch(
        setCurrentTemplate({ ...template, schemaId: selectedOption.schemaId })
      );
    }
  };

  const setCurrentTemplateDetails = () => {
    let currentTemplate = {
      eventMetaData: [],
      hasMovedToNextStep: false
    };
    currentTemplate.campaignOccurrenceType =
      thisCampaign.campaignOccurrenceType;
    currentTemplate.surveyResponseTemplate =
      thisCampaign.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE ||
      thisCampaign.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE;
    currentTemplate.productName = thisCampaign.productName;
    currentTemplate.eventTypeCategory = thisCampaign.eventTypeCategory;
    currentTemplate.productCategory = thisCampaign.productCategory;
    currentTemplate.type = thisCampaign.type;
    currentTemplate.schemaIdentifier = thisCampaign.schemaIdentifier;
    if (
      thisCampaign.campaignOccurrenceType === ON_DEMAND ||
      thisCampaign.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE ||
      thisCampaign.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE
    ) {
      currentTemplate.productType = thisCampaign.productType;
      if (thisCampaign.productType === ProductType.List) {
        currentTemplate.subscriberListId = thisCampaign.subscriberListId;
        currentTemplate.segmentedListId = thisCampaign.segmentedListId;
        currentTemplate.eventMetaData = thisCampaign.eventMetaData;
      }
    }

    if (thisCampaign?.eventType !== '') {
      currentTemplate.eventType = thisCampaign.eventType;
    }

    dispatch(
      setCurrentTemplate({ ...currentTemplate, schemaId: template.schemaId })
    );
  };

  const setCurrentAttachmentTemplateDetails = () => {
    let currentAttachmentTemplate = {
      eventMetaData: []
    };
    currentAttachmentTemplate.campaignOccurrenceType =
      thisCampaign.campaignOccurrenceType;
    currentAttachmentTemplate.productName = thisCampaign.productName;
    currentAttachmentTemplate.eventTypeCategory =
      thisCampaign.eventTypeCategory;
    currentAttachmentTemplate.productCategory = thisCampaign.productCategory;
    currentAttachmentTemplate.schemaIdentifier = thisCampaign.schemaIdentifier;
    currentAttachmentTemplate.eventMetaData = thisCampaign.eventMetaData;
    if (
      thisCampaign.campaignOccurrenceType === ON_DEMAND ||
      thisCampaign.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE ||
      thisCampaign.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE
    ) {
      currentAttachmentTemplate.productType = thisCampaign.productType;
      if (thisCampaign.productType === ProductType.List) {
        currentAttachmentTemplate.subscriberListId =
          thisCampaign.subscriberListId;
        currentAttachmentTemplate.segmentedListId =
          thisCampaign.segmentedListId;
        currentAttachmentTemplate.eventMetaData = thisCampaign.eventMetaData;
      }
    }

    if (thisCampaign?.eventType !== '') {
      currentAttachmentTemplate.eventType = thisCampaign.eventType;
    }

    dispatch(
      setCurrentAttachmentTemplate({
        ...currentAttachmentTemplate,
        schemaId: attachmentTemplate.schemaId
      })
    );
  };

  const setCurrentRecipientDetails = () => {
    let selectedOption = supportedEventTypeOptions.find(
      option => option.id === thisCampaign.schemaIdentifier
    );
    let currentQueryTemplate = {};
    currentQueryTemplate._campaignOccurrenceType =
      thisCampaign.campaignOccurrenceType;
    currentQueryTemplate.productName = thisCampaign.productName;
    currentQueryTemplate.productCategory = thisCampaign.productCategory;
    currentQueryTemplate.eventTypeCategory = thisCampaign.eventTypeCategory;
    currentQueryTemplate.schemaIdentifier = thisCampaign.schemaIdentifier;
    currentQueryTemplate.eventMetaData = thisCampaign.eventMetaData;
    currentQueryTemplate.schemaId = 'static_onDemand';
    currentQueryTemplate.partialCollectionName = selectedOption?.partialCollectionName;
    if (
      thisCampaign.campaignOccurrenceType === ON_DEMAND ||
      thisCampaign.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE ||
      thisCampaign.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE
    ) {
      currentQueryTemplate.productType = thisCampaign.productType;
      if (thisCampaign.productType === ProductType.List) {
        currentQueryTemplate.subscriberListId = thisCampaign.subscriberListId;
        currentQueryTemplate.segmentedListId = thisCampaign.segmentedListId;
        currentQueryTemplate.eventMetaData = thisCampaign.eventMetaData;
      }
    }
    dispatch(setCurrentQueryTemplate({ ...currentQueryTemplate }));
  };

  const goNext = event => {
    dispatch(
      setCurrentCampaign({
        ...thisCampaign,
        hasMovedToNextStep: event.currentTarget?.hasMovedToNextStep || false
      })
    );
    history.push(
      `${nextPath || event.currentTarget?.attributes?.link?.value}${
        campaignUuid ? `/${campaignUuid}` : ''
      }`
    );
  };

  const goBack = event => {
    dispatch(setCurrentCampaign({ ...thisCampaign }));
    history.push(
      `${backPath || event.currentTarget.attributes.link.value}${
        campaignUuid ? `/${campaignUuid}` : ''
      }`
    );
  };

  const onTabChange = () => {
    dispatch(setCurrentCampaign(thisCampaign));
  };

  const goToNextStep = () => {
    const errors = validate(true);
    if (
      !errors.name &&
      !errors.campaignOccurrenceType &&
      !errors.schemaIdentifier &&
      !errors.backport &&
      !errors.subscriberListId
    ) {
      goNext({ currentTarget: { hasMovedToNextStep: true } });
    }
  };

  const setByPath = (obj, path, value) => {
    if (typeof path === 'string') {
      path = path.split('.');
    }
    if (obj && path.length === 1) {
      obj[path] = value;
      return;
    }
    return setByPath(obj[path[0]] || {}, path.slice(1), value);
  };

  const handleProductTypeChange = event => {
    const { name, value } = event.target;
    let selectedOption;
    let campaignUpdate = produce(thisCampaign, draft => {
      draft[name] = value;
      if (draft.campaignOccurrenceType === ON_DEMAND) {
        selectedOption = supportedOnDemandTypeOptions.find(
          option => option.id === value
        );
      } else if (draft.campaignOccurrenceType === SCHEDULED) {
        selectedOption = supportedScheduleTypeOptions.find(
          option => option.id === value
        );
      } else if (draft.campaignOccurrenceType === ON_EVENT) {
        selectedOption = supportedEventTypeOptions.find(
          option => option.id === value
        );
      } else if (draft.campaignOccurrenceType === INTERNAL_SURVEY_RESPONSE) {
        selectedOption = supportedInternalSurveyResponseTypeOptions.find(
          option => option.id === value
        );
      } else if (draft.campaignOccurrenceType === EXTERNAL_SURVEY_RESPONSE) {
        selectedOption = supportedExternalSurveyResponseTypeOptions.find(
          option => option.id === value
        );
      }

      draft.eventType = selectedOption?.eventType || '';
      draft.productCategory = selectedOption?.productCategory || '';
      draft.productName = selectedOption?.productName || '';
      draft.eventTypeCategory = selectedOption?.eventTypeCategory || '';
      draft.schemaIdentifier = selectedOption?.schemaIdentifier || '';
      draft.subscriberListId = '';
      draft.segmentedListId = '';
      setByPath(draft, name, value);
    });
    dispatch(
      setCurrentTemplate({ ...template, schemaId: selectedOption.schemaId })
    );
    dispatch(
      setCurrentAttachmentTemplate({
        ...attachmentTemplate,
        schemaId: selectedOption.schemaId
      })
    );
    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };

  const handleChange = event => {
    let campaignUpdate = produce(thisCampaign, draft => {
      setByPath(draft, event.target.name, event.target.value);

      if (event.target.name === 'campaignOccurrenceType') {
        draft.productName = draft.eventType = draft.schemaIdentifier = draft.productCategory = draft.subscriberListId = draft.segmentedListId =
          '';
        draft.productType = ProductType.Product;
        if (draft.campaignOccurrenceType === 'ON_DEMAND' || draft.campaignOccurrenceType === 'EXTERNAL_SURVEY_RESPONSE') {
          draft.promotional = true;
        } else {
          draft.promotional = false;
        };
        if (draft.campaignOccurrenceType === 'EXTERNAL_SURVEY_RESPONSE') {
          draft.type = 'EMAIL';
        };
      }
      if(event.target.name === 'type') {
        if (event.target.value === 'EMAIL') {
          draft.emailCampaignData = {
            emailSubject: '',
            fromAddress: '',
            fromName: '',
            replyAddress: ''
          };
        } else if (event.target.value === 'SMS') {
          delete draft.emailCampaignData;
        }
      }
    });

    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };

  const handleEventTypeChange = event => {
    const { value } = event.target;

    const option = supportedEventTypeOptions.find(
      option => option.id === value
    );

    let campaignUpdate = produce(thisCampaign, draft => {
      setByPath(draft, 'eventType', option?.eventType || '');
      setByPath(draft, 'productCategory', option?.productCategory || '');
      setByPath(draft, 'productName', option?.productName || '');
      setByPath(draft, 'eventTypeCategory', option?.eventTypeCategory || '');
      setByPath(draft, 'schemaIdentifier', option?.schemaIdentifier || '');
      setByPath(draft, 'subscribersList', '');
    });
    dispatch(setCurrentTemplate({ ...template, schemaId: option.schemaId }));
    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };
  const handleScheduleRuleChange = values => {
    const {
      ruleName,
      value,
      scheduleUnit,
      scheduleActionDate,
      scheduleNow,
      recurrenceExpression,
      processOnCheckout,
      processOnCheckin
    } = values;
    let campaignUpdate = produce(thisCampaign, draft => {
      let selectedOption;
      switch (thisCampaign.campaignOccurrenceType) {
        case SCHEDULED: selectedOption = supportedScheduleTypeOptions.find(
          option => option.id === thisCampaign.schemaIdentifier
        );
          break;
        case ON_DEMAND:
          selectedOption = supportedOnDemandTypeOptions.find(
            option => option.id === thisCampaign.schemaIdentifier
          );
          break;
        case ON_EVENT:
          selectedOption = supportedEventTypeOptions.find(
            option => option.id === thisCampaign.schemaIdentifier
          );
          break;
        case INTERNAL_SURVEY_RESPONSE:
          selectedOption = supportedInternalSurveyResponseTypeOptions.find(
            option => option.id === thisCampaign.schemaIdentifier
          );
          break;
        case EXTERNAL_SURVEY_RESPONSE:
          selectedOption = supportedExternalSurveyResponseTypeOptions.find(
            option => option.id === thisCampaign.schemaIdentifier
          );
          break;
      }
      let { partialCollectionName = '' } = selectedOption || {};

      // Backend needs to be updated to not require eventType when
      // we are using a scheduleRule. In the mean time they requested be set as such
      if (ruleName && !value) {
        draft.eventName = 'ReservationCreate';
        draft.scheduleRule = {
          [ruleName]: null,
          scheduleUnit: scheduleUnit || null,
          ...(processOnCheckout && { processOnCheckout: processOnCheckout.value}),
          ...(processOnCheckin && { processOnCheckin: processOnCheckin.value}),
          partialCollectionName
        };
      } else if (value && ruleName) {
        draft.scheduleRule[ruleName] = value;
      } else if (scheduleUnit) {
        draft.scheduleRule.scheduleUnit = scheduleUnit;
      } else if (scheduleActionDate) {
        draft.scheduleRule = {
          scheduleActionDate: scheduleActionDate
        };
      } else if (recurrenceExpression) {
        draft.scheduleRule = {
          recurrenceExpression: recurrenceExpression
        };
      } else if (scheduleNow) {
        draft.scheduleRule = {
          scheduleNow: scheduleNow
        };
      } else if (processOnCheckout) {
        draft.scheduleRule.processOnCheckout = processOnCheckout.value;
      } else if (processOnCheckin) {
        draft.scheduleRule.processOnCheckin = processOnCheckin.value;
      } else {
        draft.scheduleRule = {};
      }
    });

    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };

  const removeDefaultTemplate = () => {
    let campaignUpdate = produce(thisCampaign, draft => {
      delete draft.communicationTemplateUuid;
    });

    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };

  const removeRuleBasedTemplate = () => {
    let campaignUpdate = produce(thisCampaign, draft => {
      delete draft.templates;
    });

    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };

  const addAttachment = () => {
    let campaignUpdate = produce(thisCampaign, draftCampaign => {
      draftCampaign.attachmentTemplateUuids = Array.isArray(
        draftCampaign.attachmentTemplateUuids
      )
        ? draftCampaign.attachmentTemplateUuids
        : [];

      draftCampaign.attachmentTemplateUuids.push('');
    });
    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };

  const updateAttachment = (e, i) => {
    let campaignUpdate = produce(thisCampaign, draftCampaign => {
      draftCampaign.attachmentTemplateUuids[i] = e.target.value;
    });
    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };

  const removeAttachment = i => {
    let campaignUpdate = produce(thisCampaign, draftCampaign => {
      draftCampaign.attachmentTemplateUuids.splice(i, 1);
    });
    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };

  const saveCampaign = () => {
    const errors = validate(true);
    if (errors.meta.clean) {
      dispatch(setCurrentCampaign({ ...thisCampaign }));

      if (thisCampaign.uuid) {
        dispatch(updateCampaign(thisCampaign));
      } else {
        dispatch(createCampaign());
      }
      formController = new FormController();
    } else {
      dispatch(resetScheduleNow());
    }
  };

  const closeCampaign = () => {
    dispatch(unsetCurrentTemplate());
    formController = new FormController();
    history.push('/campaigns');
  };

  const onBlur = e => {
    refreshSessionIfNeeded();
    formController.onBlur(e);
    validate();
  };

  const onFocus = e => {
    refreshSessionIfNeeded();
    formController.onFocus(e);
    validate();
  };

  const shouldShowError = name => {
    return formController.shouldShowError(validationErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(validationErrors, name);
  };

  const showFormSaveError = () => {
    return formController.showFormSaveError(validationErrors);
  };

  const handleTypeChange = event => {
    if (event.target.value === ProductType.List || event.target.value === ProductType.GUEST_INSIGHTS) {
      dispatch(getSchemas({ showActive: true }));
    }
    setThisCampaign({
      ...thisCampaign,
      productType: event.target.value,
      eventType: '',
      productCategory: '',
      productName: '',
      eventTypeCategory: '',
      schemaIdentifier: '',
      subscriberListId: '',
      segmentedListId: ''
    });
  };

  const handleSubscriberListChange = (event, option) => {
    if (!option) {
      setThisCampaign({
        ...thisCampaign,
        subscriberListId: '',
        segmentedListId: ''
      });
      return;
    }
    setThisCampaign({
      ...thisCampaign,
      ...(option.listId
        ? { subscriberListId: option.listId, segmentedListId: option.id }
        : { subscriberListId: option.id, segmentedListId: '' })
    });
  };

  const handleDelayUntilNextDayChange = () => {
    let selectedOption = supportedEventTypeOptions.find(
      option => option.id === thisCampaign.schemaIdentifier
    );
    let campaignUpdate = produce(thisCampaign, draftCampaign => {
      let delayUntilNextDay = !thisCampaign.scheduleRule?.delayUntilNextDay;
      if (delayUntilNextDay) {
        draftCampaign.scheduleRule = {
          ...draftCampaign.scheduleRule,
          partialCollectionName: selectedOption.partialCollectionName,
          delayUntilNextDay: delayUntilNextDay
        }
      } else {
        delete draftCampaign.scheduleRule.delayUntilNextDay;
        if (!draftCampaign.scheduleRule?.delayDeliveryTime) {
          delete draftCampaign.scheduleRule;
        }
      }
    });
    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };

  const handleDelayDeliveryTimeChange = (delayDeliveryTime) => {
    let selectedOption = supportedEventTypeOptions.find(
      option => option.id === thisCampaign.schemaIdentifier
    );
    let campaignUpdate = produce(thisCampaign, draftCampaign => {
      let timeString = convertToTimeOnlyString(delayDeliveryTime, true);
      if (timeString) {
        draftCampaign.scheduleRule = {
          ...draftCampaign.scheduleRule,
          partialCollectionName: selectedOption.partialCollectionName,
          delayDeliveryTime: timeString
        }
      } else {
        delete draftCampaign.scheduleRule.delayDeliveryTime;
        if (!draftCampaign.scheduleRule?.delayUntilNextDay) {
          delete draftCampaign.scheduleRule;
        }
      }
    });
    setThisCampaign(campaignUpdate);
    validate(false, campaignUpdate);
  };

  return {
    thisCampaign,
    validationErrors,
    onFocus,
    onBlur,
    showFormSaveError,
    getErrorMessage,
    shouldShowError,
    validate,
    handleChange,
    goNext,
    goBack,
    onTabChange,
    goToNextStep,
    saveCampaign,
    closeCampaign,
    isLoading,
    isCampaignSaved,
    isCampaignSaving,
    removeDefaultTemplate,
    removeRuleBasedTemplate,
    handleScheduleRuleChange,
    handleProductTypeChange,
    addAttachment,
    updateAttachment,
    removeAttachment,
    attachments,
    supportedScheduleTypeOptions,
    supportedOnDemandTypeOptions,
    supportedEventTypeOptions,
    supportedInternalSurveyResponseTypeOptions,
    supportedExternalSurveyResponseTypeOptions,
    handleEventTypeChange,
    productIntegrationOptions,
    setCurrentTemplateDetails,
    setCurrentAttachmentTemplateDetails,
    setCurrentRecipientDetails,
    isItineraryOnly,
    isCampaignReadOnly,
    handleTypeChange,
    handleSubscriberListChange,
    handleDelayUntilNextDayChange,
    handleDelayDeliveryTimeChange
  };
}
